import { useEffect, useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import apiClient from '../../lib/api';
import { toast } from '../../lib/toast';
import {
  STOREFRONT_PRESETS,
  HEADING_FONTS,
  BODY_FONTS,
  DEFAULT_PRESET_ID,
  getPreset,
  resolveStorefrontTheme,
  type StorefrontTheme,
} from '../../lib/storefrontTheme';

interface Props {
  initial?: StorefrontTheme | null;
  onSaved?: () => void | Promise<void>;
}

/** Load the Google Fonts for the live preview (same mechanism as the shell). */
function usePreviewFonts(href: string | null) {
  useEffect(() => {
    if (!href) return;
    if (document.head.querySelector(`link[data-store-fonts="${href}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute('data-store-fonts', href);
    document.head.appendChild(link);
  }, [href]);
}

export function StorefrontThemeEditor({ initial, onSaved }: Props) {
  const [presetId, setPresetId] = useState(initial?.presetId || DEFAULT_PRESET_ID);
  const [accentColor, setAccentColor] = useState<string | undefined>(initial?.accentColor);
  const [headingFont, setHeadingFont] = useState<string | undefined>(initial?.headingFont);
  const [bodyFont, setBodyFont] = useState<string | undefined>(initial?.bodyFont);
  const [saving, setSaving] = useState(false);

  const theme: StorefrontTheme = { presetId, accentColor, headingFont, bodyFont };
  const resolved = useMemo(() => resolveStorefrontTheme(theme), [presetId, accentColor, headingFont, bodyFont]);
  usePreviewFonts(resolved.googleFontsHref);

  const preset = getPreset(presetId);
  const currentHeadingKey = headingFont || preset.headingFontKey;
  const currentBodyKey = bodyFont || preset.bodyFontKey;

  // The theme currently live on the storefront (what's saved). Kept separate
  // from the in-progress edits so the user can always see and snap back to it.
  const liveTheme: StorefrontTheme = {
    presetId: initial?.presetId || DEFAULT_PRESET_ID,
    accentColor: initial?.accentColor,
    headingFont: initial?.headingFont,
    bodyFont: initial?.bodyFont,
  };
  const liveResolved = resolveStorefrontTheme(liveTheme);
  const livePreset = getPreset(liveTheme.presetId);
  const liveHeadingLabel = HEADING_FONTS.find(f => f.key === (liveTheme.headingFont || livePreset.headingFontKey))?.label;
  const liveBodyLabel = BODY_FONTS.find(f => f.key === (liveTheme.bodyFont || livePreset.bodyFontKey))?.label;

  const dirty = JSON.stringify({ presetId, accentColor, headingFont, bodyFont }) !== JSON.stringify(liveTheme);

  const revertToCurrent = () => {
    setPresetId(liveTheme.presetId!);
    setAccentColor(liveTheme.accentColor);
    setHeadingFont(liveTheme.headingFont);
    setBodyFont(liveTheme.bodyFont);
  };

  // Selecting a preset resets the overrides so the pure preset shows; the user
  // can then tweak accent/fonts on top.
  const selectPreset = (id: string) => {
    setPresetId(id);
    setAccentColor(undefined);
    setHeadingFont(undefined);
    setBodyFont(undefined);
  };

  const save = async () => {
    setSaving(true);
    const res = await apiClient.updateStorefrontTheme({ presetId, accentColor, headingFont, bodyFont });
    setSaving(false);
    if (res.success) {
      toast.success('Storefront theme saved');
      await onSaved?.();
    } else {
      toast.error(res.error || 'Failed to save theme');
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
      <h2 className="text-base font-semibold text-gray-900">Storefront theme</h2>
      <p className="text-sm text-gray-500 mt-1">Pick a preset, then optionally fine-tune the accent color and fonts.</p>

      {/* Current (live) theme — the saved version, always visible to snap back to */}
      <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-gray-50 border border-gray-200 px-3 py-1.5 text-xs text-gray-600">
        <span className="font-medium text-gray-500">Current:</span>
        <span className="w-3.5 h-3.5 rounded-full border border-black/10" style={{ background: liveResolved.accent }} />
        <span className="font-medium text-gray-800">{livePreset.label}</span>
        <span className="text-gray-400">·</span>
        <span className="font-mono">{liveResolved.accent}</span>
        <span className="text-gray-400">·</span>
        <span>{liveHeadingLabel} / {liveBodyLabel}</span>
      </div>

      {/* Presets */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
        {STOREFRONT_PRESETS.map((p) => {
          const selected = p.id === presetId;
          const isLive = p.id === liveTheme.presetId;
          return (
            <button
              key={p.id}
              onClick={() => selectPreset(p.id)}
              className={`relative text-left rounded-xl border p-3 transition-colors ${
                selected ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {isLive && (
                <span className="absolute top-1.5 right-1.5 text-[9px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5">
                  Current
                </span>
              )}
              <div className="flex items-center gap-1.5 h-8 rounded-md overflow-hidden mb-2 border" style={{ borderColor: p.border }}>
                <div className="flex-1 h-full" style={{ background: p.bg }} />
                <div className="w-6 h-full" style={{ background: p.accent }} />
                <div className="w-4 h-full" style={{ background: p.text }} />
              </div>
              <div className="flex items-center gap-1">
                <span className="text-sm font-medium text-gray-900">{p.label}</span>
                {selected && <Check className="w-3.5 h-3.5 text-blue-600 ml-auto" />}
              </div>
              <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">{p.description}</p>
            </button>
          );
        })}
      </div>

      {/* Accent + fonts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Accent color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={resolved.accent}
              onChange={(e) => setAccentColor(e.target.value)}
              className="w-10 h-10 rounded-md border border-gray-200 cursor-pointer bg-white p-0.5"
              aria-label="Accent color"
            />
            <input
              type="text"
              value={resolved.accent}
              onChange={(e) => {
                const v = e.target.value.trim();
                if (/^#[0-9a-fA-F]{6}$/.test(v)) setAccentColor(v);
              }}
              className="flex-1 border border-gray-200 rounded-md px-3 py-2 text-sm font-mono"
            />
          </div>
          {accentColor && (
            <button onClick={() => setAccentColor(undefined)} className="text-[11px] text-gray-500 underline mt-1">
              Reset to preset accent
            </button>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Heading font</label>
          <select
            value={currentHeadingKey}
            onChange={(e) => setHeadingFont(e.target.value)}
            className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm"
          >
            {HEADING_FONTS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Body font</label>
          <select
            value={currentBodyKey}
            onChange={(e) => setBodyFont(e.target.value)}
            className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm"
          >
            {BODY_FONTS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
        </div>
      </div>

      {/* Live preview */}
      <div className="mt-5">
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Preview</label>
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: resolved.border }}>
          <div style={{ background: resolved.bg, color: resolved.text }} className="p-5">
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium"
                style={{ background: resolved.accent, color: resolved.accentForeground, fontFamily: resolved.headingFamily }}
              >
                A
              </div>
              <div style={{ fontFamily: resolved.headingFamily }} className="text-lg font-medium">Acme Wellness</div>
            </div>
            <div style={{ fontFamily: resolved.headingFamily }} className="text-2xl font-semibold mt-4">A curated formulary</div>
            <p style={{ fontFamily: resolved.bodyFamily, color: resolved.muted }} className="text-sm mt-2 leading-relaxed">
              Each medication has been selected by your clinic. Read what it does, how it works, and whether it's right for you.
            </p>
            <button
              className="mt-4 px-4 py-2 rounded-lg text-sm font-medium"
              style={{ background: resolved.accent, color: resolved.accentForeground, fontFamily: resolved.bodyFamily }}
            >
              Request a consult
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-5">
        <button
          onClick={save}
          disabled={!dirty || saving}
          className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            !dirty || saving ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {saving ? 'Saving…' : dirty ? 'Save theme' : 'Saved'}
        </button>
        {dirty && (
          <button
            onClick={revertToCurrent}
            disabled={saving}
            className="px-4 py-2.5 rounded-lg text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Revert to current
          </button>
        )}
        {dirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
      </div>
    </div>
  );
}
